/**
 * Owner: Masa Bayu
 * File: group.ts
 * Date:Time: 2026-10-14 02:47
 * Description: Paragraph grouper · ported baseline algorithm · no profile import.
 */

import {
  detectNativeDropCapPairs,
  nativeDropCapSkipIds,
} from './dropcap.js';
import { MIN_BODY_WORDS } from './geometry.js';
import type { SpsuTextLine } from './types.js';

const X_ALIGN_TOL_PT = 2;
const FONT_SIZE_TOL_PT = 0.5;
const LINE_HEIGHT_MIN = 1.15;
const LINE_HEIGHT_MAX = 1.85;
const MIN_BODY_WIDTH = 160;
const MAX_BODY_FONT = 16;

const TOC_DOT_LEADER = /\.{3,}|…|\.(?:\s*\.){2,}/;
const TOC_PAGE_NUM = /^\d{1,4}$/;
const TOC_MARKER = /^[➢▸►▶•▪◆※➔➜➡➤➥➔]+$/u;

function sameX(a: SpsuTextLine, b: SpsuTextLine): boolean {
  return Math.abs(a.bbox.x - b.bbox.x) <= X_ALIGN_TOL_PT;
}

function sameFont(a: SpsuTextLine, b: SpsuTextLine): boolean {
  return Math.abs(a.style.fontSize - b.style.fontSize) <= FONT_SIZE_TOL_PT;
}

function consistentLineHeight(prev: SpsuTextLine, next: SpsuTextLine): boolean {
  const dy = next.bbox.y - prev.bbox.y;
  if (!(dy > 0)) return false;
  const em = prev.style.fontSize > 0 ? prev.style.fontSize : prev.bbox.height;
  if (!(em > 0)) return false;
  const ratio = dy / em;
  return ratio >= LINE_HEIGHT_MIN && ratio <= LINE_HEIGHT_MAX;
}

function isPageNumberPlate(line: SpsuTextLine): boolean {
  return TOC_PAGE_NUM.test(line.content.trim());
}

export function isTocStyleLine(
  line: SpsuTextLine,
  all: readonly SpsuTextLine[],
): boolean {
  const trimmed = line.content.trim();
  if (!trimmed) return false;
  if (TOC_DOT_LEADER.test(trimmed)) return true;
  if (isPageNumberPlate(line)) return true;
  if (TOC_MARKER.test(trimmed)) return true;
  if (TOC_DOT_LEADER.test(trimmed) && /\d{1,4}\s*$/.test(trimmed)) return true;

  const em = line.style.fontSize > 0 ? line.style.fontSize : line.bbox.height;
  const yTol = Math.max(3, em * 0.4);
  const lookAhead = em * LINE_HEIGHT_MAX;
  const titleRight = line.bbox.x + line.bbox.width;

  for (const other of all) {
    if (other.id === line.id) continue;
    const dy = other.bbox.y - line.bbox.y;
    const otherIsPage = isPageNumberPlate(other);
    const toTheRight = other.bbox.x >= titleRight - 1;

    if (otherIsPage && Math.abs(dy) <= yTol && toTheRight) return true;
    if (otherIsPage && dy > 0 && dy <= lookAhead && toTheRight) return true;

    if (
      TOC_MARKER.test(other.content.trim()) &&
      Math.abs(dy) <= yTol &&
      other.bbox.x < line.bbox.x
    ) {
      return true;
    }
  }
  return false;
}

export function isBodyLineCandidate(
  line: SpsuTextLine,
  all: readonly SpsuTextLine[] = [],
): boolean {
  const trimmed = line.content.trim();
  if (trimmed.length < 12) return false;
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < MIN_BODY_WORDS) return false;
  if (line.bbox.width < MIN_BODY_WIDTH) return false;
  if (!(line.style.fontSize > 0) || line.style.fontSize >= MAX_BODY_FONT) {
    return false;
  }
  if (all.length > 0 && isTocStyleLine(line, all)) return false;
  return true;
}

function canAppendBody(
  prev: SpsuTextLine,
  next: SpsuTextLine,
  all: readonly SpsuTextLine[],
): boolean {
  if (!isBodyLineCandidate(next, all)) return false;
  if (!sameFont(prev, next) || !consistentLineHeight(prev, next)) return false;
  if (sameX(prev, next)) return true;
  if (prev.bbox.x > next.bbox.x + X_ALIGN_TOL_PT) return true;
  return false;
}

/**
 * Port of groupNativeLinesIntoParagraphs.
 * Dropcap groups first · TOC/title fail-closed as singletons · body join.
 */
export function groupLinesIntoParagraphs(
  lines: readonly SpsuTextLine[],
): SpsuTextLine[][] {
  if (lines.length === 0) return [];

  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const sorted = [...lines].sort(
    (a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x,
  );
  const pairs = detectNativeDropCapPairs(sorted);
  const dropSkip = nativeDropCapSkipIds(pairs);
  const consumed = new Set<string>();
  const groups: SpsuTextLine[][] = [];

  for (const pair of pairs) {
    const group: SpsuTextLine[] = [pair.letter, pair.host];
    for (const id of pair.absorbIds) {
      const plate = byId.get(id);
      if (plate) group.push(plate);
    }
    group.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
    for (const el of group) consumed.add(el.id);

    let cursor =
      group.filter((g) => g.id !== pair.letter.id).at(-1) ?? pair.host;
    for (const cand of sorted) {
      if (consumed.has(cand.id)) continue;
      if (cand.bbox.y <= cursor.bbox.y) continue;
      const em = cursor.style.fontSize > 0 ? cursor.style.fontSize : 11;
      if (cand.bbox.y - cursor.bbox.y > em * LINE_HEIGHT_MAX) break;
      if (!isBodyLineCandidate(cand, sorted)) break;
      if (!sameFont(cursor, cand) || !consistentLineHeight(cursor, cand)) break;
      const atHost =
        Math.abs(cand.bbox.x - pair.host.bbox.x) <= X_ALIGN_TOL_PT;
      const atColumn =
        Math.abs(cand.bbox.x - pair.letter.bbox.x) <= X_ALIGN_TOL_PT;
      if (!atHost && !atColumn) break;
      group.push(cand);
      consumed.add(cand.id);
      cursor = cand;
    }
    groups.push(group);
  }

  let current: SpsuTextLine[] | null = null;
  for (const line of sorted) {
    if (consumed.has(line.id) || dropSkip.has(line.id)) continue;
    if (!isBodyLineCandidate(line, sorted)) {
      /* EKOR perenggan · baris pendek ragged di tepi lajur (fon + tinggi baris
       * + x sepadan) menutup perenggan yang berjalan · BUKAN perenggan baharu.
       * Port baseline group-native-paragraphs.ts · tanpa ini Design D melabel
       * baris penuhnya 'single'. */
      const tail = current?.[current.length - 1];
      if (
        tail !== undefined &&
        sameFont(tail, line) &&
        consistentLineHeight(tail, line) &&
        sameX(tail, line) &&
        !isTocStyleLine(line, sorted)
      ) {
        current!.push(line);
        continue;
      }
      groups.push([line]);
      continue;
    }
    if (current && canAppendBody(current[current.length - 1]!, line, sorted)) {
      current.push(line);
      continue;
    }
    current = [line];
    groups.push(current);
  }

  groups.sort(
    (a, b) =>
      (a[0]?.bbox.y ?? 0) - (b[0]?.bbox.y ?? 0) ||
      (a[0]?.bbox.x ?? 0) - (b[0]?.bbox.x ?? 0),
  );

  return groups;
}
